<template>
  <div class="portada-arley" ref="contenedorXV" aria-label="Agradecimiento de XV Años">
    <div class="fondo-gradiente">
      <div class="fondo-arco"></div>
    </div>

    <div class="onda-luz-bg"></div>

    <img src="@/assets/brillo.avif" class="brillo brillo-izq-1" alt="" />
    <img src="@/assets/brillo.avif" class="brillo brillo-izq-2" alt="" />
    <img src="@/assets/brillo.avif" class="brillo brillo-der-1" alt="" />
    <img src="@/assets/brillo.avif" class="brillo brillo-der-2" alt="" />

    <img src="@/assets/cor.png" class="flor-grande flor-der-1" alt="" />

    <div class="bloque-agradecimiento-editorial">
      <p class="texto-gratitud-padres">
        Con amor y gratitud a mis padres, cuyo cuidado y apoyo han hecho posible para mí este momento tan especial.
      </p>

      <img src="@/assets/sepp.avif" class="separador-vintage-center" aria-hidden="true" alt="" />

      <div class="bloque-nombres-padres">
        <h2 class="nombre-caligrafico">Oscar Ramos Paz </h2>
        <span class="conector-and">&</span>
        <h2 class="nombre-caligrafico"> Claudia Morales Muñoz</h2>
      </div>
    </div>

    <div class="rasgado rasgado-inferior"></div>
  </div>
</template>

<script>
import { onMounted, ref, nextTick, onUnmounted } from 'vue'
import gsap from 'gsap'

export default {
  name: 'PortadaArley',
  setup() {
    // Cambio de nombre de la referencia raíz para evitar colisiones globales en Vue
    const contenedorXV = ref(null)
    let observadorGlobal = null
    let contextoGsap = null

    onMounted(async () => {
      await nextTick()
      if (!contenedorXV.value) return

      // Usamos el selector local de GSAP limitado estrictamente a este componente
      const $ = gsap.utils.selector(contenedorXV.value)

      // Selección ultra segura de elementos locales
      const fondo = $('.fondo-gradiente')
      const arco = $('.fondo-arco')
      const textosEditorial = $('.texto-gratitud-padres, .separador-vintage-center, .bloque-nombres-padres')
      const floresNormales = $('.velas-superior, .flor-superior-derecha')
      const floresGrandes = $('.flor-grande')
      const brillos = $('.brillo')

      // Configuración de estados iniciales controlados (Evita saltos visuales en móviles)
      const totalElementos = [].concat(fondo, arco, textosEditorial, floresNormales, floresGrandes, brillos)
      gsap.set(totalElementos, { autoAlpha: 0, y: 35 })
      gsap.set(brillos, { scale: 0 })

      // IntersectionObserver optimizado para el viewport de dispositivos móviles
      observadorGlobal = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          // Ajuste fino: Se dispara cuando un 25% de la sección es visible, pero compensado por el rootMargin
          if (!e.isIntersecting) return

          // Creamos un contexto de GSAP para aislar por completo los 'tweens' e hilos de animación
          contextoGsap = gsap.context(() => {
            const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

            // 1. Entrada de fondo y arquitectura del arco
            tl.to(fondo, { autoAlpha: 1, y: 0, duration: 0.9 })
              .to(arco, { autoAlpha: 1, y: 0, duration: 0.8 }, '-=0.5')

            // 2. Revelado elegante y cinematográfico de los textos
            tl.to(textosEditorial, {
              autoAlpha: 1,
              y: 0,
              duration: 1,
              stagger: 0.2
            }, '-=0.3')

            // 3. Entrada de la flora superior decorativa
            tl.to(floresNormales, {
              autoAlpha: 1,
              y: 0,
              duration: 0.9,
              stagger: 0.15
            }, '-=0.7')

            // 4. Flores laterales con rotación orgánica controlada
            tl.to(floresGrandes, {
              autoAlpha: 1,
              y: 0,
              rotate: () => gsap.utils.random(-5, 5),
              duration: 0.9,
              stagger: 0.12
            }, '-=0.5')

            // 5. Encendido e inicio de bucle infinito para brillos luxury
            tl.to(brillos, {
              autoAlpha: 1,
              scale: 1,
              duration: 0.8,
              stagger: 0.15,
              onComplete: () => {
                gsap.to(brillos, {
                  opacity: 0.6,
                  scale: 1.12,
                  duration: 1,
                  repeat: -1,
                  yoyo: true,
                  ease: 'sine.inOut'
                })
              }
            }, '-=0.4')

            // Bucle flotante orgánico para mantener vivas las flores secundarias
            const todasLasFlores = [].concat(floresNormales, floresGrandes)
            todasLasFlores.forEach((flor, i) => {
              if (!flor) return
              gsap.to(flor, {
                y: `+=${gsap.utils.random(4, 9)}`,
                x: `+=${gsap.utils.random(-4, 4)}`,
                rotation: `+=${gsap.utils.random(-1, 1)}`,
                duration: gsap.utils.random(3.2, 4.8),
                repeat: -1,
                yoyo: true,
                ease: 'sine.inOut',
                delay: i * 0.1
              })
            })

          }, contenedorXV.value)

          // Dejamos de observar para ejecutar la animación de entrada una sola vez de forma limpia
          observadorGlobal.disconnect()
        })
      }, { 
        threshold: 0.20,
        rootMargin: "0px 0px -10% 0px" // Truco Pro: Fuerza a que en móviles deba subir 10% más para activarse ante los ojos del usuario
      })

      observadorGlobal.observe(contenedorXV.value)
    })

    // Limpieza estricta del DOM al destruir el componente
    onUnmounted(() => {
      if (observadorGlobal) observadorGlobal.disconnect()
      if (contextoGsap) contextoGsap.revert()
    })

    return { contenedorXV }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@400;500;700&family=Great+Vibes&display=swap');

.portada-arley {
  position: relative;
  width: 100%;
  min-height: 60vh;
  background:
    linear-gradient(
      rgba(241, 198, 219, 0.813),
      rgba(242, 186, 214, 0.616)
    ),
    url('@/assets/fnn.avif') center center / cover no-repeat;
  display: flex;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 80px 24px 120px;
  box-sizing: border-box;
}

/* FONDO (GRADIENTE Y ARCO) */
.fondo-gradiente {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(180deg, #d2b4a3 0%, #c4a18d 60%, #fff 100%);
  z-index: -2;
}

.fondo-arco {
  position: absolute;
  top: 15vh;
  left: 50%;
  transform: translateX(-50%);
  width: min(92vw, 360px);
  height: 80vh;
  border-radius: 180px 180px 0 0;
  background: #fdf2f3;
  z-index: -1;
}

/* 🌟 DESTELLO BLANCO RECTO VERTICAL PASANDO EN EL FONDO */
.onda-luz-bg {
  position: absolute;
  top: 0;
  left: -50%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0) 45%,
    rgba(255, 255, 255, 0.9) 50%,
    rgba(255, 255, 255, 0) 55%,
    rgba(255, 255, 255, 0) 100%
  );
  z-index: -1;
  animation: barridoLuzVertical 5s infinite linear;
  pointer-events: none;
}

@keyframes barridoLuzVertical {
  0% { transform: translateX(-40%); }
  100% { transform: translateX(90%); }
}

/* 🌟 BLOQUE DE TEXTOS DE AGRADECIMIENTO (DISEÑO IDÉNTICO A IMAGE_003F39.PNG) */
.bloque-agradecimiento-editorial {
  width: 100%;
  max-width: 440px;
  text-align: center;
  z-index: 5;
  margin: auto 0;
  display: flex;
  margin-top:80px;
  flex-direction: column;
  align-items: center;
}

.texto-gratitud-padres {
  font-family: 'Playfair Display', serif;
  font-size: clamp(16px, 4vw, 20px);
  color: #2c2c2c;
  line-height: 1.65;
  margin: 0 0 24px 0;
  font-weight: 400;
  padding: 0 10px;
}

.separador-vintage-center {
  width: min(50vw, 180px);
  height: auto;
  object-fit: contain;
  margin-bottom: 28px;
  opacity: 0.85;
}

.bloque-nombres-padres {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.nombre-caligrafico {
  font-family: 'titulos';
  font-weight: 400;
  font-size: clamp(42px, 10vw, 38px);
  color: #ffffff;
  margin: 0;
  letter-spacing: 1px;
  line-height: 1.2;
}

.conector-and {
font-family: 'Cormorant', serif;  font-size: clamp(52px, 7vw, 58px);
  color: #fefefe;
  margin: -5px 0;
  font-style: italic;
  font-weight: 400;
  line-height: 1;
}

/* ELEMENTOS GRÁFICOS */
.flor, .flor-grande, .adorno, .brillo {
  position: absolute;
  transform-origin: center;
  will-change: transform, opacity;
  object-fit: contain;
}

.adorno.velas-superior {
  top: -210px; right: -220px; z-index: 1;
  width: min(550vw, 550px);
  transform: rotate(8deg);
}

.flor-superior-derecha {
  top: 10px; right: -20px; z-index: 2;
  width: min(45vw, 190px); /* Ajustado tamaño para simular la flor grande blanca de la esquina */
}

/* 3 FLORES IZQUIERDAS */
.flor-der-1 {
  top: -6vh; right:-4vw !important; z-index: 13;
  width: min(44vw, 150px) !important;
}

/* BRILLOS */
.brillo-izq-1 {
  top: -5px; left: -18vw; z-index: 0;
  width: 320px; opacity: 0.6;
}
.brillo-izq-2 {
  bottom: 28vh; left: 5vw; z-index: 4;
  width: 100px; opacity: 0.5;
}
.brillo-der-1 {
  top: 22vh; right: 6vw; z-index: 0;
  width: 110px; opacity: 0.6;
}
.brillo-der-2 {
  bottom: 7vh; right: -3vh; z-index: 4;
  width: 140px; opacity: 0.7;
}

/* RASGADOS */
.rasgado {
  position: absolute;
  left: 0;
  width: 100%;
  height: 50px;
  background: #ffffff;
  z-index: 3;
  margin-top: -40px;
  pointer-events: none;
}

.rasgado-inferior {
  bottom: 0;
  overflow: visible;
  background:
    linear-gradient(
          rgba(241, 198, 219, 0.813),
      rgba(208, 152, 180, 0.616)
    ),
    url('@/assets/fn.avif') center center / cover no-repeat;
  clip-path: polygon(
    0% 20%, 25% 40%, 50% 15%, 75% 45%, 100% 25%,
    100% 100%, 0% 100%
  );
}

/* RESPONSIVE */
@media (min-width: 992px) {
  .portada-arley { padding-top: 64px; }
}

@media (max-width: 500px) {
  .flor-izq-1, .flor-izq-2, .flor-izq-3 { left: -14vw; }
  .flor-der-1, .flor-der-2, .flor-der-3 { right: -14vw; }
}
</style>